import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { zql } from '../schema.js';
import { applyCursor } from '../util/apply-cursor.js';
import { getPatientSearchWords, normalizePatientIdentifier, toPatientIdentifierPattern } from '../util/patients.js';
import { inUseGfrQuery } from '../util/patient-gfr.js';
import { KEPT_DOSE_STATUSES, RECALCULATED_STATUSES } from '../util/gfr-dose-changes.js';
import { LOCAL_ID_SYSTEM } from '../util/constants.js';
import { DoseCalcModeActive, PatientSource } from '../database/schema.js';
import { Cursor, Sort } from './util.js';
import { EventLogResources } from '../mutators/util.js';
import { patientEventsQuery } from './event-log.queries.js';
export const ListPatientsFiltersInput = z.object({
    name: z.string().optional(),
    responsibleDoctor: z.array(z.uuidv7()).optional(),
    source: z.array(PatientSource).optional(),
});
export const ListPatientsInput = z.object({
    cursor: Cursor.extend({
        createdAt: z.number(),
        sortableGivenName: z.string().optional(),
        sortableFamilyName: z.string().optional(),
        birthDate: z.number().optional(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
    limit: z.number().optional(),
    filters: ListPatientsFiltersInput.optional(),
});
export const ListInsuranceCompaniesInput = z.object({
    cursor: Cursor.extend({
        createdAt: z.number(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
    limit: z.number().optional(),
});
export const GetPatientTherapiesInput = z.object({
    patientId: z.string(),
    protocolIds: z.array(z.string()).optional(),
    // will hide the prescriptions without a protocol
    hideIsolated: z.boolean().optional(),
    // will hide past prescriptions
    hidePrevious: z.boolean().optional(),
});
export const GetTherapySequenceSiblingsInput = z.object({
    patientProtocolId: z.string(),
    excludeId: z.string(),
});
export const FindPatientsByIdentifiersInput = z.object({
    nationalId: z.string().optional(),
    hospitalPatientId: z.string().optional(),
    insuranceNumber: z.string().optional(),
    excludePatientId: z.string().optional(),
    /** defaults to local patients (manual and imported); the HL7 match panel asks for pending HL7 messages */
    sources: z.array(PatientSource).optional(),
});
function searchPatients(q, search) {
    const words = getPatientSearchWords(search);
    if (!words.length)
        return q;
    // every typed word must appear in the patient's search text, so "Surname Firstname" finds the
    // patient whose names are stored "Firstname Surname"
    return q.where(({ and, cmp }) => and(...words.map(word => cmp('searchText', 'ILIKE', `%${word}%`))));
}
function filterPatients(q, filters) {
    if (!filters)
        return q;
    const { responsibleDoctor, source = ['manual', 'hl7_import'] } = filters;
    q = searchPatients(q, filters.name);
    if (responsibleDoctor?.length) {
        q = q.whereExists('responsibleDoctor', q => q.where(({ and, cmp }) => and(cmp('id', 'IN', responsibleDoctor))));
    }
    q = q.where('source', 'IN', source);
    return q;
}
export const patientsQueries = {
    listPatients: defineQuery(ListPatientsInput, ({ args }) => {
        let q = zql.patients.where('deletedAt', 'IS', null);
        q = filterPatients(q, args.filters);
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        q = q
            .related('identifiers', r => r.where('system', '=', LOCAL_ID_SYSTEM).orderBy('createdAt', 'desc').limit(1))
            .related('familyDoctor')
            .related('responsibleDoctor')
            .related('contacts', r => r
            .related('address')
            .where(({ cmp }) => cmp('contactType', '=', 'home'))
            .orderBy('createdAt', 'desc')
            .orderBy('updatedAt', 'desc')
            .limit(1))
            .related('encounters', r => r
            .orderBy('createdAt', 'desc')
            .limit(1)
            // .related('section')
            .related('ward')
        // .related('physician')
        // .related('customer')
        );
        return q;
    }),
    listGlobalSearchPatients: defineQuery(ListPatientsInput, ({ args }) => {
        let q = zql.patients.where('deletedAt', 'IS', null);
        q = filterPatients(q, args.filters);
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        q = q.related('characteristics', c => c.related('diagnosis'));
        return q;
    }),
    getPatientById: defineQuery(z.string(), ({ args }) => {
        return zql.patients
            .related('responsibleDoctor', r => r.related('userAccessLocations', a => a.related('location').related('section').related('ward')))
            .related('familyDoctor')
            .related('identifiers', r => r.where('system', '=', LOCAL_ID_SYSTEM).orderBy('createdAt', 'desc').limit(1))
            .related('contacts', r => r
            .related('address')
            .where(({ or, cmp }) => or(cmp('contactType', '=', 'home'), cmp('contactType', '=', 'emergency')))
            .orderBy('createdAt', 'desc')
            .orderBy('updatedAt', 'desc')
            .limit(2))
            .related('gfrs', r => r.where('deletedAt', 'IS', null).orderBy('labTime', 'desc').orderBy('createdAt', 'desc').limit(1))
            .related('insurance', r => r.related('insuranceCompany'))
            .related('characteristics', r => r.orderBy('createdAt', 'desc').limit(1).related('diagnosis'))
            .related('encounters', r => r.orderBy('createdAt', 'desc').limit(1).related('section').related('ward').related('customer'))
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getPatientHistory: defineQuery(z.string(), ({ args }) => {
        return patientEventsQuery(args)
            .related('actor', r => r.related('userAccessLocations', a => a.related('location').related('section').related('ward')))
            .orderBy('performedAt', 'desc');
    }),
    getPatientLatestCharacteristics: defineQuery(z.string(), ({ args }) => {
        return zql.patientCharacteristics
            .where('patientId', '=', args)
            .related('diagnosis')
            .orderBy('createdAt', 'desc')
            .limit(1)
            .one();
    }),
    getPatientLatestEncounter: defineQuery(z.string(), ({ args }) => {
        return zql.patientEncounters
            .where('patientId', '=', args)
            .orderBy('createdAt', 'desc')
            .related('ward', ward => ward.related('location'))
            .related('section')
            .limit(1)
            .one();
    }),
    getPatientLatestGfr: defineQuery(z.string(), ({ args }) => {
        return inUseGfrQuery(args);
    }),
    getPatientAucDoses: defineQuery(z.string(), ({ args }) => {
        return zql.patientTherapySequenceMedicine
            .where('patientId', '=', args)
            .where('calculationMethod', '=', DoseCalcModeActive.enum.AUC)
            .where('deletedAt', 'IS', null)
            .whereExists('therapySequence', sequence => sequence.where('deletedAt', 'IS', null))
            .related('therapySequence')
            .related('activeSubstance', drug => drug.related('activeSubstanceDetails', d => d.related('doseUnit')).related('supportiveMedicationDetails'));
    }),
    // doses a characteristics change recalculates or keeps (CY-379)
    getPatientRecalculationDoses: defineQuery(z.string(), ({ args }) => {
        return zql.patientTherapySequenceMedicine
            .where('patientId', '=', args)
            .where('deletedAt', 'IS', null)
            .whereExists('therapySequence', sequence => sequence.where('status', 'IN', [...RECALCULATED_STATUSES, ...KEPT_DOSE_STATUSES]).where('deletedAt', 'IS', null))
            .related('therapySequence')
            .related('activeSubstance', drug => drug.related('activeSubstanceDetails', d => d.related('doseUnit')).related('supportiveMedicationDetails'));
    }),
    getPatientGfrs: defineQuery(z.string(), ({ args }) => {
        return zql.patientGfrs
            .where('patientId', '=', args)
            .where('deletedAt', 'IS', null)
            .orderBy('labTime', 'desc')
            .orderBy('createdAt', 'desc');
    }),
    getPatientCharacteristicsHistory: defineQuery(z.string(), ({ args }) => {
        return zql.patientCharacteristics.where('patientId', '=', args).orderBy('createdAt', 'desc');
    }),
    getPatientProtocols: defineQuery(z.string(), ({ args }) => {
        return zql.patientProtocols
            .where('patientId', '=', args)
            .where('deletedAt', 'IS', null)
            .orderBy('createdAt', 'desc')
            .related('protocol', p => p.related('diagnosis'))
            .related('created');
    }),
    // Not removed and not stopped protocols with their prescriptions, for the Add protocol overlap warning.
    getActivePatientProtocolsWithTreatments: defineQuery(z.string(), ({ args }) => {
        return zql.patientProtocols
            .where('patientId', '=', args)
            .where('deletedAt', 'IS', null)
            .where('endDate', 'IS', null)
            .orderBy('startDate', 'asc')
            .related('protocol')
            .related('therapySequence', sequence => sequence.where('deletedAt', 'IS', null));
    }),
    getPatientTherapies: defineQuery(GetPatientTherapiesInput, ({ args }) => {
        let q = zql.patientTherapySequence.where('patientId', '=', args.patientId);
        if (args.protocolIds) {
            q = q.where(({ cmp, or }) => or(cmp('patientProtocolId', 'IN', args.protocolIds || []), cmp('patientProtocolId', 'IS', null)));
        }
        if (args.hidePrevious) {
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            q = q.where('therapyDate', '>=', now.getTime());
        }
        if (args.hideIsolated) {
            q = q.where('patientProtocolId', 'IS NOT', null);
        }
        return q
            .orderBy('therapyDate', 'asc')
            .orderBy('time', 'asc')
            .related('protocol', p => p.related('diagnosis'))
            .related('patientProtocol')
            .related('carrierSolution')
            .related('applicationForm')
            .related('observations', obs => obs.where('deletedAt', 'IS', null))
            .related('order')
            .related('medicine', med => med
            .related('activeSubstance', q => q
            .related('activeSubstanceDetails', d => d.related('doseUnit'))
            .related('supportiveMedicationDetails', d => d.related('doseUnit')))
            .related('prescribedName', prescribedName => prescribedName.related('drugCatalogItem', drug => drug.related('activeSubstanceDetails')))
            .orderBy('createdAt', 'asc'))
            .where('deletedAt', 'IS', null);
    }),
    getTherapySequenceSiblings: defineQuery(GetTherapySequenceSiblingsInput, ({ args }) => {
        return zql.patientTherapySequence
            .where('patientProtocolId', '=', args.patientProtocolId)
            .where('id', '!=', args.excludeId)
            .where('deletedAt', 'IS', null)
            .orderBy('therapyDate', 'asc');
    }),
    listInsuranceCompanies: defineQuery(ListInsuranceCompaniesInput, ({ args }) => {
        let q = zql.insuranceCompanies.where('deletedAt', 'IS', null);
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        return q;
    }),
    /**
     * Patients using a national ID, hospital ID or insurance number: local patients for the duplicate check on
     * add/edit patient, pending HL7 messages for the HL7 match panel. Results over-match
     * (see `toPatientIdentifierPattern`) and must be confirmed by the caller.
     */
    findPatientsByIdentifiers: defineQuery(FindPatientsByIdentifiersInput, ({ args }) => {
        const nationalId = normalizePatientIdentifier(args.nationalId);
        const hospitalPatientId = normalizePatientIdentifier(args.hospitalPatientId);
        const insuranceNumber = normalizePatientIdentifier(args.insuranceNumber);
        let q = zql.patients
            .where('deletedAt', 'IS', null)
            .where('source', 'IN', args.sources ?? [PatientSource.enum.manual, PatientSource.enum.hl7_import])
            .related('insurance');
        if (!nationalId && !hospitalPatientId && !insuranceNumber)
            return q.limit(0);
        if (args.excludePatientId)
            q = q.where('id', '!=', args.excludePatientId);
        return q.where(({ or, cmp, exists }) => {
            const conditions = [];
            if (nationalId)
                conditions.push(cmp('nationalId', 'ILIKE', toPatientIdentifierPattern(nationalId)));
            if (hospitalPatientId)
                conditions.push(cmp('hospitalPatientId', 'ILIKE', toPatientIdentifierPattern(hospitalPatientId)));
            if (insuranceNumber)
                conditions.push(exists('insurance', r => r.where('insuranceNumber', 'ILIKE', toPatientIdentifierPattern(insuranceNumber))));
            return or(...conditions);
        });
    }),
    getPatientGfrActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', EventLogResources.patientGfr)
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
};
//# sourceMappingURL=patient.queries.js.map